<script setup lang="ts">
import ErrorBoundary from "@/components/ErrorBoundary.vue";
import { useAuthStore } from "@/stores/auth";
import { useBatchImportStore } from "@/stores/batchImport";
import { useFavoritesStore } from "@/stores/favorites";
import { useJobsStore } from "@/stores/jobs";
import { useNotesStore } from "@/stores/notes";
import { useNotificationsStore } from "@/stores/notifications";
import { useQueueStore } from "@/stores/queue";
import { useResultsStore } from "@/stores/results";
import { useRolesStore } from "@/stores/roles";
import { useSharesStore } from "@/stores/shares";
import { useTagsStore } from "@/stores/tags";
import { socketService } from "@/utils/socket";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const authStore = useAuthStore();
const jobsStore = useJobsStore();
const resultsStore = useResultsStore();
const favoritesStore = useFavoritesStore();
const batchImportStore = useBatchImportStore();
const tagsStore = useTagsStore();
const notesStore = useNotesStore();
const sharesStore = useSharesStore();
const notificationsStore = useNotificationsStore();
const queueStore = useQueueStore();
const rolesStore = useRolesStore();

const sessionCheckFailed = ref(false);
const checkingSession = ref(false);

const initializeSession = async () => {
  // Check if user is logged in
  if (authStore.token) {
    checkingSession.value = true;
    try {
      await authStore.fetchCurrentUser();
      sessionCheckFailed.value = false;
    } catch {
      sessionCheckFailed.value = true;
      return;
    } finally {
      checkingSession.value = false;
    }

    // If fetchCurrentUser failed (token invalid), redirect to login
    if (!authStore.user) {
      router.push("/login");
      return;
    }

    // Load roles after user is authenticated
    await rolesStore.fetchRoles();

    socketService.connect();

    // Setup auth WebSocket listeners
    authStore.setupWebSocketListeners();

    // Initialize WebSocket listeners for stores
    jobsStore.initializeWebSocket();
    resultsStore.initializeWebSocket();
    favoritesStore.setupWebSocketListeners();
    // Phase 22.3: Batch import listeners now in socket.ts
    tagsStore.setupWebSocketListeners(socketService.socket!);
    notesStore.setupWebSocketListeners(socketService.socket!);
    sharesStore.setupWebSocketListeners(socketService.socket!);

    // Fetch initial notification data
    await Promise.all([
      notificationsStore.fetchNotifications({ limit: 20 }),
      notificationsStore.fetchStats(),
      notificationsStore.fetchPreferences(),
    ]);

    // Setup WebSocket listeners for notifications
    notificationsStore.setupWebSocketListeners();

    // Initialize queue store
    await queueStore.fetchQueueStats();
  }
};

onMounted(initializeSession);
</script>

<template>
  <div id="app">
    <ErrorBoundary>
      <div v-if="sessionCheckFailed" class="min-h-screen flex flex-col items-center justify-center gap-4 p-6" role="alert">
        <p>Die Sitzung konnte gerade nicht geprüft werden. Bitte versuche es erneut.</p>
        <button class="btn btn-primary" :disabled="checkingSession" @click="initializeSession">
          {{ checkingSession ? "Wird geprüft …" : "Erneut versuchen" }}
        </button>
      </div>
      <RouterView v-else />
    </ErrorBoundary>
  </div>
</template>
